import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Mail, Lock, Shield } from 'lucide-react';
import { LOGIN_STORE } from '../graphql/queries';

const TOKEN_KEY = 'field_assist_token';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [login, { loading }] = useMutation(LOGIN_STORE);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const { data } = await login({ variables: { email, password } });
      const r = data?.loginStore;
      if (!r || r.error) {
        setError(r?.message || 'Login failed');
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, r.token);
      // Signals AppShell to fire the "Welcome back, …" toast once. AppShell
      // consumes + removes this flag on mount, so a plain refresh of any
      // page (where this flag isn't set) won't re-trigger the toast.
      sessionStorage.setItem('show_welcome', 'true');
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Connection error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
          <Shield size={22} />
        </div>
        <div className="flex items-baseline gap-1 mb-1">
          <span className="text-text font-black text-xl">Field</span>
          <span className="bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent font-black text-xl">
            Assist
          </span>
        </div>
        <p className="text-xs text-text2 mb-6">Sign in to your store</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="input-label">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" />
              <input
                type="email"
                className="input-field pl-9"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@store.com"
              />
            </div>
          </div>
          <div>
            <label className="input-label">Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" />
              <input
                type="password"
                className="input-field pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
};
